<template>
	<v-container fluid>
		<v-row class="mb-4" align="center">
			<v-col>
				<h2 class="text-primary text-h5 font-weight-medium">
					Modèles des produits ({{ filteredModels.length }}/{{ totalModels }})
				</h2>
			</v-col>

			<v-col cols="auto">
				<v-btn color="primary" prepend-icon="mdi-plus" @click="createModel">
					Nouveau Modèle
				</v-btn>
			</v-col>
		</v-row>

		<v-card rounded="xl" elevation="2" class="mb-4">
			<v-card-text>
				<v-row dense>
					<v-col cols="12" md="4">
						<v-select
							v-model="selectedMake"
							:items="productMakes"
							label="Filtrer par marque"
							clearable
							density="compact"
						/>
					</v-col>

					<v-col cols="12" md="4">
						<v-text-field
							v-model="selectedModel"
							label="Filtrer par modèle"
							clearable
							density="compact"
						/>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>

		<v-card rounded="xl" elevation="2">
			<v-data-table :items="filteredModels" :loading="loading" item-key="id" hover class="product-model-table">
				<template #headers>
					<tr>
						<th>Marque</th>
						<th>Modèle</th>
					</tr>
				</template>

				<template #item="{ item }">
					<tr class="table-row" @click="goToEditor(item)">
						<td>
							<div class="font-weight-medium">
								{{ item.productMake }}
							</div>
						</td>

						<td>
							<div class="font-weight-medium">
								{{ item.model }}
							</div>
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card>
	</v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { fetchProductModels } from '@/utils/product-model-api'
import { ProductMake } from '@/types'
import type { ProductModel } from '@/types/product-model'

const router = useRouter()

const models = ref<ProductModel[]>([])
const loading = ref(false)
const selectedMake = ref<ProductMake | null>(null)
const selectedModel = ref('')

const totalModels = computed(() => models.value.length)
const productMakes = Object.values(ProductMake)
const filteredModels = computed(() => {
	if (!selectedMake.value) {
		return models.value.filter((model) => {
			const modelFilter = selectedModel.value.trim().toLowerCase()
			return !modelFilter || model.model.toLowerCase().includes(modelFilter)
		})
	}

	return models.value.filter((model) => {
		const modelFilter = selectedModel.value.trim().toLowerCase()
		const matchesMake = model.productMake === selectedMake.value
		const matchesModel = !modelFilter || model.model.toLowerCase().includes(modelFilter)
		return matchesMake && matchesModel
	})
})

async function fetchModels() {
	loading.value = true
	try {
		models.value = await fetchProductModels()
	} finally {
		loading.value = false
	}
}

function goToEditor(model: ProductModel) {
	router.push({
		name: 'ProductModelEditor',
		params: { id: model.id }
	})
}

function createModel() {
	router.push({
		name: 'ProductModelEditor',
		params: { id: 'new' }
	})
}

onMounted(fetchModels)
</script>

<style scoped>
.table-row {
	cursor: pointer;
	transition: background 0.15s ease;
}

.table-row:hover {
	background: rgba(var(--v-theme-primary), 0.05);
}
</style>
